import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import patientService from '../../services/patientService';
import PatientDetails from '../PracticeManagement/PatientManagement/PatientDetails';
import './PatientDashboard.css';

/**
 * Patient login home — resolves the logged-in patient's practice/patient IDs
 * and renders the same Patient Details screen used by staff.
 */
const PatientDashboard = () => {
  const { user } = useSelector((state) => state.auth);
  const [practiceId, setPracticeId] = useState(null);
  const [patientId, setPatientId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const resolvePatientContext = async () => {
      if (!user?.id) {
        setLoading(false);
        setError('Please sign in again.');
        return;
      }

      try {
        setLoading(true);
        setError(null);

        let pid = user.practice_id || user.practices?.[0]?.id;
        let uid = user.patient_id;

        if (!pid || !uid) {
          const res = await patientService.getCurrentPatient();
          if (res.data?.success && res.data.data) {
            pid = res.data.data.practice_id;
            uid = res.data.data.id;
          }
        }

        if (cancelled) return;

        if (!pid || !uid) {
          throw new Error('Patient record not found. Please contact your healthcare provider.');
        }

        setPracticeId(pid);
        setPatientId(uid);
      } catch (err) {
        if (cancelled) return;
        console.error('Patient dashboard resolve error:', err);
        const msg =
          err.response?.data?.message ||
          err.message ||
          'Failed to load patient details';
        setError(msg);
        toast.error(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    resolvePatientContext();
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (loading) {
    return (
      <div className="patient-dashboard-loading-container">
        <FontAwesomeIcon icon={faSpinner} spin className="patient-dashboard-loading-spinner" />
        <p className="patient-dashboard-loading-text">Loading your patient details…</p>
      </div>
    );
  }

  if (error || !practiceId || !patientId) {
    return (
      <div className="patient-dashboard-error-container">
        <div className="patient-dashboard-error-message">
          {error || 'Unable to load patient details.'}
        </div>
      </div>
    );
  }

  return <PatientDetails practiceId={practiceId} patientId={patientId} />;
};

export default PatientDashboard;
